import { useState, useCallback, useRef } from "react";

export interface AnimationStep {
  id: string;
  name: string;
  duration: number;
  easing?: string;
  delay?: number;
  transform?: {
    translateX?: string | number;
    translateY?: string | number;
    scale?: number;
    rotate?: string;
    rotateY?: string;
  };
  opacity?: number;
  onStart?: () => void;
  onComplete?: () => void;
}

export interface AnimationWorkflow {
  id: string;
  name: string;
  steps: AnimationStep[];
  onWorkflowComplete?: () => void;
}

interface AnimationState {
  currentStep: number;
  isRunning: boolean;
  currentTransform: string;
  currentOpacity: number;
  currentDuration: number;
  currentEasing: string;
}

export const useAnimationWorkflow = (
  initialOpacity: number = 1,
  initialTransform: string = "",
) => {
  const [animationState, setAnimationState] = useState<AnimationState>({
    currentStep: -1,
    isRunning: false,
    currentTransform: initialTransform,
    currentOpacity: initialOpacity,
    currentDuration: 0,
    currentEasing: "ease-in-out",
  });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentWorkflowRef = useRef<AnimationWorkflow | null>(null);

  const buildTransform = (transform: AnimationStep["transform"]): string => {
    if (!transform) return "";

    const parts: string[] = [];

    if (transform.translateX !== undefined) {
      const x =
        typeof transform.translateX === "number"
          ? `${transform.translateX}px`
          : transform.translateX;
      parts.push(`translateX(${x})`);
    }

    if (transform.translateY !== undefined) {
      const y =
        typeof transform.translateY === "number"
          ? `${transform.translateY}px`
          : transform.translateY;
      parts.push(`translateY(${y})`);
    }

    if (transform.scale !== undefined) {
      parts.push(`scale(${transform.scale})`);
    }

    if (transform.rotate !== undefined) {
      parts.push(`rotate(${transform.rotate})`);
    }

    if (transform.rotateY !== undefined) {
      parts.push(`rotateY(${transform.rotateY})`);
    }

    return parts.join(" ");
  };

  const executeStep = useCallback(
    (step: AnimationStep, stepIndex: number, workflow: AnimationWorkflow) => {
      const transform = buildTransform(step.transform);

      setAnimationState((prev) => {

        return {
          ...prev,
          currentStep: stepIndex,
          currentTransform: transform,
          currentOpacity:
            step.opacity !== undefined ? step.opacity : prev.currentOpacity,
          currentDuration: step.duration,
          currentEasing: step.easing || "ease-in-out",
        };
      });

      // Call onStart callback
      if (step.onStart) {
        step.onStart();
      }

      // Schedule next step or completion
      const totalTime = step.duration + (step.delay || 0);

      // For zero-duration steps, use a small delay to ensure CSS is applied
      const schedulingDelay = totalTime === 0 ? 10 : totalTime;

      timeoutRef.current = setTimeout(() => {
        // Call onComplete callback for current step
        if (step.onComplete) {
          step.onComplete();
        }

        // Check if there are more steps
        if (stepIndex < workflow.steps.length - 1) {
          executeStep(workflow.steps[stepIndex + 1], stepIndex + 1, workflow);
        } else {
          // Workflow complete
          setAnimationState((prev) => ({
            ...prev,
            isRunning: false,
            currentStep: -1,
          }));

          if (workflow.onWorkflowComplete) {
            workflow.onWorkflowComplete();
          }
        }
      }, schedulingDelay);
    },
    [],
  );

  const stopWorkflow = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setAnimationState((prev) => ({
      ...prev,
      isRunning: false,
      currentStep: -1,
    }));

    currentWorkflowRef.current = null;
  }, []);

  const startWorkflow = useCallback(
    (workflow: AnimationWorkflow) => {
      // Use a ref check instead of state to avoid timing issues with React strict mode
      if (timeoutRef.current !== null) {
        return;
      }

      if (animationState.isRunning) {
        return;
      }

      // Also check if we already have an active workflow
      if (currentWorkflowRef.current !== null) {
        stopWorkflow();
      }

      currentWorkflowRef.current = workflow;

      setAnimationState((prev) => ({
        ...prev,
        isRunning: true,
        currentStep: 0,
      }));

      if (workflow.steps.length > 0) {
        // Small delay to ensure state update is processed
        setTimeout(() => {
          if (currentWorkflowRef.current === workflow) {
            // Double check we're still the active workflow
            executeStep(workflow.steps[0], 0, workflow);
          }
        }, 1);
      }
    },
    [animationState.isRunning, executeStep, stopWorkflow],
  );

  const resetWorkflow = useCallback(() => {
    stopWorkflow();
    setAnimationState({
      currentStep: -1,
      isRunning: false,
      currentTransform: initialTransform,
      currentOpacity: initialOpacity,
      currentDuration: 0,
      currentEasing: "ease-in-out",
    });
  }, [stopWorkflow, initialOpacity, initialTransform]);

  return {
    animationState,
    startWorkflow,
    stopWorkflow,
    resetWorkflow,
    isRunning: animationState.isRunning,
    currentStep: animationState.currentStep,
    // Style properties for the animated element
    animatedStyle: {
      transform: animationState.currentTransform,
      opacity: animationState.currentOpacity,
      transition:
        animationState.isRunning && animationState.currentDuration > 0
          ? `all ${animationState.currentDuration}ms ${animationState.currentEasing}`
          : animationState.currentDuration === 0
          ? "none"
          : "all 0ms ease-in-out",
    },
  };
};
